// Local, non-secret user preferences: appearance, language, and install
// dismissals. Only these three keys are persisted; nothing private (session
// data, claims, tokens, or API responses) is ever written to storage.

export type ColorSchemePreference = 'light' | 'dark' | 'auto';
export type LanguagePreference = 'en' | 'hr';

export const SUPPORTED_LANGUAGES = ['en', 'hr'] as const;

const KEYS = {
  colorScheme: 'sensing.colorScheme',
  language: 'sensing.language',
} as const;

/** Read a value defensively: private-mode Safari and disabled storage throw. */
function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Write a value defensively; a failed write must never break the UI. */
function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* Storage unavailable; the preference applies for this session only. */
  }
}

export function readColorScheme(): ColorSchemePreference {
  const value = read(KEYS.colorScheme);
  return value === 'light' || value === 'dark' || value === 'auto' ? value : 'auto';
}

export function saveColorScheme(value: ColorSchemePreference): void {
  write(KEYS.colorScheme, value);
}

export function readLanguage(): LanguagePreference | null {
  const value = read(KEYS.language);
  return value === 'en' || value === 'hr' ? value : null;
}

export function saveLanguage(value: LanguagePreference): void {
  write(KEYS.language, value);
}

/** Resolve the initial language: saved preference, then browser, then English. */
export function resolveInitialLanguage(): LanguagePreference {
  return readLanguage() ?? systemLanguage() ?? 'en';
}

/** The first supported language among the browser's, or null if none is. */
export function systemLanguage(): LanguagePreference | null {
  const candidates =
    typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language]);
  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const base = candidate.toLowerCase().split('-')[0];
    const match = SUPPORTED_LANGUAGES.find((language) => language === base);
    if (match) return match;
  }
  return null;
}

/** Whether the OS currently prefers a dark scheme. */
export function prefersDarkScheme(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}
